import { completeAuthRedirect } from '@/lib/auth-redirect'

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import GenieLogo from '@/components/ui/icon/icons/GenieLogo'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Link } from '@tanstack/react-router'
import { useAuth } from '@/hooks/useAuth'
import { toast } from '@/components/ui/toast'
import { describeInviteError } from '@/lib/api/invite-errors'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.genie.art'

interface InviteAcceptProps {
  slug: string
}

interface InviteData {
  code: string
  status: string
  expiresAt: string
}

export function InviteAccept({ slug }: InviteAcceptProps) {
  const navigate = useNavigate()
  const { user, isLoading: authLoading, isAuthenticated } = useAuth()
  const [inviteData, setInviteData] = useState<InviteData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLookingUp, setIsLookingUp] = useState(true)
  const [isAccepting, setIsAccepting] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)

  useEffect(() => {
    setIsHydrated(true)
  }, [])

  // Look up the invite by slug
  useEffect(() => {
    async function lookupInvite() {
      try {
        const response = await fetch(`${API_BASE_URL}/api/invite/lookup/${slug}`)
        const data = await response.json()

        if (!response.ok) {
          setError(data.error || 'Invite not found')
          return
        }

        setInviteData(data.data)
      } catch {
        setError('Failed to load invite. Please try again.')
      } finally {
        setIsLookingUp(false)
      }
    }

    lookupInvite()
  }, [slug])

  const acceptInvite = useCallback(async () => {
    if (!inviteData?.code) return

    setIsAccepting(true)
    try {
      const response = await fetch(`${API_BASE_URL}/api/invite/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: inviteData.code }),
      })

      const data = await response.json()

      if (!response.ok) {
        // wave-2 codes: ALREADY_REFERRED (409), INVITE_UNAVAILABLE/EXPIRED (410), NOT_FOUND (404)
        toast.error(describeInviteError(data.code, data.error, 'Failed to accept invite'))
        setIsAccepting(false)
        return
      }

      toast.success('Invite accepted! Welcome to Genie.')
      completeAuthRedirect('/workspace')
    } catch {
      toast.error('Failed to accept invite. Please try again.')
      setIsAccepting(false)
    }
  }, [inviteData, navigate])

  // No auto-accept. Accepting an invite attributes the account to a referrer
  // and starts paying them commission, so it only ever happens on an explicit
  // click — merely opening /invite/<slug> while signed in must not claim it.

  // Show loading during hydration or invite lookup
  if (!isHydrated || isLookingUp) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center flex flex-col items-center">
          <GenieLogo className="mb-6 h-12 w-12" />
          <p className="mb-4">Loading invite...</p>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
        </div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="md:bg-muted flex items-center justify-center bg-cover md:min-h-screen">
        <div className="flex h-screen w-full flex-col md:h-auto md:max-w-[440px] md:px-4 md:py-6">
          <Card className="rounded-none p-6 md:rounded-xl">
            <CardHeader className="mb-6 flex flex-col items-start border-b-0 p-0 text-left">
              <Link to="/">
                <GenieLogo className="mb-6 h-12 w-12" />
              </Link>
              <CardTitle className="text-lg font-bold">Invite Unavailable</CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Button size="lg" className="w-full" onClick={() => navigate({ to: '/login' })}>
                Go to Sign In
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  // If not authenticated, show sign-in prompt with invite context
  if (!authLoading && !isAuthenticated) {
    return (
      <div className="md:bg-muted flex items-center justify-center bg-cover md:min-h-screen">
        <div className="flex h-screen w-full flex-col md:h-auto md:max-w-[440px] md:px-4 md:py-6">
          <Card className="rounded-none p-6 md:rounded-xl">
            <CardHeader className="mb-6 flex flex-col items-start border-b-0 p-0 text-left">
              <Link to="/">
                <GenieLogo className="mb-6 h-12 w-12" />
              </Link>
              <CardTitle className="text-lg font-bold">You&apos;ve Been Invited!</CardTitle>
              <CardDescription>
                Create an account to accept this invite and join Genie.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Button
                size="lg"
                className="w-full"
                onClick={() => navigate({ to: '/signup', search: { redirect: `/invite/${slug}` } })}
              >
                Accept Invite
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  // Show auth loading
  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center flex flex-col items-center">
          <GenieLogo className="mb-6 h-12 w-12" />
          <p className="mb-4">Checking authentication...</p>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
        </div>
      </div>
    )
  }

  // Authenticated — show accept invite
  return (
    <div className="md:bg-muted flex items-center justify-center bg-cover md:min-h-screen">
      <div className="flex h-screen w-full flex-col md:h-auto md:max-w-[440px] md:px-4 md:py-6">
        <Card className="rounded-none p-6 md:rounded-xl">
          <CardHeader className="mb-6 flex flex-col items-start border-b-0 p-0 text-left">
            <Link to="/">
              <GenieLogo className="mb-6 h-12 w-12" />
            </Link>
            <CardTitle className="text-lg font-bold">Accept Invite</CardTitle>
            <CardDescription>
              Hi {user?.name || 'there'}! Click below to accept this invite and get started.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Button
              size="lg"
              className="w-full"
              onClick={acceptInvite}
              disabled={isAccepting}
            >
              {isAccepting && (
                <svg
                  className="mr-2 h-4 w-4 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              Accept Invite
            </Button>
            <Button
              variant="ghost"
              size="lg"
              className="mt-2 w-full"
              disabled={isAccepting}
              onClick={() => completeAuthRedirect('/workspace')}
            >
              Not now
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
